/**
 * The setup preflight — every prerequisite "Run full setup" needs, checked WITHOUT WRITING
 * (SCH-2236).
 *
 * GET /v1/mobile-apps/:id/setup/preflight[?ref=]                 (super-admin) for an app row
 * GET /v1/mobile-apps/setup/preflight?schoolId=<publicId>[&ref=]  (super-admin) before the row exists
 *
 * One check per prerequisite that lives outside the app's own steps: the vaulted App Store Connect
 * key, the stored Apple and Google portal sessions and the renewal lane, the Play account's service
 * account and API access, the platform keys (Expo, OneSignal, Datadog, OpenRouter, Stream, the
 * webhook secrets), the hosted browser runner, the release line the app will ship on and its
 * compliance sign-offs, the listing text, the App Review demo account, and the feature flags.
 *
 * `fail` stops the graph at the nodes it names in `blocks`; `warn` is worth reading but stops
 * nothing. Every non-passing check carries `fix`, the next action. The dashboard runs it before
 * showing the Run full setup button as ready.
 */

import { z } from 'zod'
import { SetupGraphNodeIdSchema } from './setup-graph'

export const SETUP_PREFLIGHT_GROUPS = [
  'app',
  'apple',
  'google',
  'platform',
  'runner',
  'release',
  'listing',
  'flags'
] as const
export const SetupPreflightGroupSchema = z.enum(SETUP_PREFLIGHT_GROUPS)
export type SetupPreflightGroup = z.infer<typeof SetupPreflightGroupSchema>

export const SetupPreflightStatusSchema = z.enum(['pass', 'warn', 'fail'])
export type SetupPreflightStatus = z.infer<typeof SetupPreflightStatusSchema>

export const SetupPreflightCheckSchema = z.object({
  id: z.string(),
  group: SetupPreflightGroupSchema,
  title: z.string(),
  status: SetupPreflightStatusSchema,
  /** What was observed — never a secret. */
  detail: z.string(),
  /** The next action when the check does not pass; null when it does. */
  fix: z.string().nullable(),
  /** The graph nodes that would stop on this check. */
  blocks: z.array(SetupGraphNodeIdSchema)
})
export type SetupPreflightCheck = z.infer<typeof SetupPreflightCheckSchema>

export const SetupPreflightSchema = z.object({
  schoolId: z.string(),
  schoolName: z.string().nullable(),
  /** Null for the school-level variant, before an app row exists. */
  appId: z.string().nullable(),
  /** Every check passed or only warned: Run full setup can be pressed. */
  ready: z.boolean(),
  checkedAt: z.string(),
  counts: z.record(SetupPreflightStatusSchema, z.number()),
  checks: z.array(SetupPreflightCheckSchema)
})
export type SetupPreflight = z.infer<typeof SetupPreflightSchema>

export const GetSetupPreflightResponseSchema = z.object({
  data: SetupPreflightSchema,
  message: z.string()
})
export type GetSetupPreflightResponse = z.infer<typeof GetSetupPreflightResponseSchema>

/** Optional on both variants: the git ref a drive would build from, checked instead of the default. */
export const SetupPreflightRefQuerySchema = z.object({
  ref: z.string().trim().min(1).max(200).optional()
})
export type SetupPreflightRefQuery = z.infer<typeof SetupPreflightRefQuerySchema>

export const GetSchoolSetupPreflightQuerySchema = SetupPreflightRefQuerySchema.extend({
  /** `School.publicId`. */
  schoolId: z.string().min(1),
  /** A Firebase project id chosen for the app instead of the one derived from the school's slug. */
  firebaseProjectId: z.string().trim().min(6).max(30).optional()
})
export type GetSchoolSetupPreflightQuery = z.infer<typeof GetSchoolSetupPreflightQuerySchema>
